import { publish } from '~/event-bus';
import { getCartSectionNames, throttle, classes } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

customElements.define(
  'product-form',
  class ProductForm extends HTMLCustomElement {
    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      this.on('submit', this.targets.form, this.handleFormSubmit.bind(this));
      this.on('change', this.targets.option && this.targets.option.all, this.handleOptionChange.bind(this));
      this.on('change', this.targets['variant-selector'], this.handleVariantChange.bind(this));
      this.on('change', this.targets['gift-card-form-checkbox'], this.handleGiftCardFormCheckbox.bind(this));
      this.on('scroll', window, throttle(this.handleWindowScroll.bind(this)), null, true);
      this.on('click', this.targets['floating-button'], this.handleFloatingButtonEvent.bind(this));

      this.on('click', this.targets.reviews, () => {
        const section = document.querySelector('reviewsio-product-reviews-widget');

        if (section) {
          section.scrollIntoView();
        }
      });

      this.on(
        'change',
        this.targets['us-size-type-toggle'],
        (event) => {
          this.setSelectedSizeType(
            (event ? event.target.checked : this.targets['us-size-type-toggle'].checked) ? 'US' : 'UK'
          );
        },
        null,
        true
      );
    }

    /**
     * Applies the selected size type to filter option values.
     * @param {String} type - The size type.
     */
    setSelectedSizeType(type) {
      if (!this.targets.option || !type) return;

      this.targets.option.all.forEach((option) => {
        const types = option.dataset.sizeTypes.split(', ');

        option.hidden = !types.includes(type);
      });

      /**
       * Checks the first available option for the current set of option values.
       */
      requestAnimationFrame(() => {
        if (this.targets.option.all.find((option) => option.checked && !option.disabled)) {
          return;
        }

        const defaultOptionValue = this.targets.option.all.find((option) => !option.disabled);

        if (defaultOptionValue) {
          defaultOptionValue.checked = true;

          /**
           * Emit the native event.
           */
          defaultOptionValue.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /**
     * Returns the product data, if exists.
     * @returns {Object}
     */
    getProductData() {
      if (!this.targets.data) {
        return null;
      }

      return Object.freeze(JSON.parse(this.targets.data.innerHTML));
    }

    /**
     * Returns the selected variant's identifier.
     * @returns {String}
     */
    getSelectedVariantId() {
      if (!this.targets['variant-selector']) {
        return;
      }

      return Number(this.targets['variant-selector'].value);
    }

    /**
     * Returns the selected variant object, if available.
     * @returns {Object|false}
     */
    getSelectedVariant() {
      const product = this.getProductData();

      if (!product) {
        return false;
      }

      return product.variants.find((variant) => {
        return variant.id === this.getSelectedVariantId();
      });
    }

    /**
     * Sets the form message state.
     * @param {String} text - The message to set.
     */
    setMessage(text = '') {
      if (!this.targets.message) {
        return;
      }

      this.targets.message.innerHTML = text;
    }

    /**
     * Sets the form's "adding" state, when adding to cart.
     * @param {Boolean} remove - Removes the adding state.
     */
    setAddingState(remove = false) {
      if (!this.targets.submit) return;

      if (remove) {
        this.targets.submit.innerHTML =
          this.targets.submit.dataset.defaultText ?? theme.strings.sections.main_product.add_to_cart;
      } else {
        this.targets.submit.innerHTML = theme.strings.sections.main_product.adding_to_cart;
      }

      this.targets.submit.disabled = !remove;
    }

    /**
     * Handles the form submit event.
     */
    handleFormSubmit(event) {
      const formData = new FormData(event.target);
      const product = this.getProductData();

      event.preventDefault();

      this.setAddingState();
      this.setMessage();

      formData.append('sections', getCartSectionNames());

      fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.message) {
            this.setMessage(response.message);
            return;
          }

          publish('cart:update', { sections: response.sections });
          publish('cart:added', { productId: product.id });

          this.targets.quantity.value = 1;
        })
        .finally(() => {
          this.setAddingState(true);
        })
        .catch(() => {
          event.target.submit();
        });
    }

    /**
     * Handles the option change event.
     */
    handleOptionChange() {
      const selectedVariant = this.getVariantBySelectedOptions();

      if (!selectedVariant) {
        return;
      }

      this.targets['variant-selector'].value = selectedVariant.id;

      this.targets['variant-selector'].dispatchEvent(new Event('change'));
    }

    /**
     * Handles the variant change event.
     * - Sets the form state based on the selected variant.
     */
    handleVariantChange() {
      const selected = this.getSelectedVariant();

      if (!selected) {
        return;
      }

      this.dispatchEvent(
        new CustomEvent('variant', {
          detail: selected,
        })
      );
    }

    /**
     * Returns the selected option values.
     * @returns {Object}
     */
    getSelectedOptions() {
      const selected = {};

      if (this.targets.option) {
        this.targets.option.all.forEach((element) => {
          const name = element.name;

          if (element.checked) {
            selected[name] = element.value;
          }
        });
      }

      return selected;
    }

    /**
     * Returns the variant object by its selected options.
     * @returns {Object}
     */
    getVariantBySelectedOptions() {
      const selectedOptions = this.getSelectedOptions();
      const product = this.getProductData();

      return product.variants.find((variant) => {
        return Object.keys(selectedOptions).every((key, index) => variant.options[index] === selectedOptions[key]);
      });
    }

    /**
     * Handles the gift card recipient form checkbox change.
     * @param {InputEvent} event - The checkbox event.
     */
    handleGiftCardFormCheckbox(event) {
      if (this.targets['gift-card-input']) {
        this.targets['gift-card-input'].all.forEach((element) => {
          if (event.target.checked) {
            element.classList.remove('is-hidden');
            return;
          }

          element.classList.add('is-hidden');
        });
      }
    }

    /**
     * Handles the window scroll event.
     */
    handleWindowScroll() {
      if (this.targets.floating) {
        const { bottom: submitBottom } = this.targets.submit.getBoundingClientRect();
        const { bottom: mainBottom } = this.closest('main').getBoundingClientRect();

        this.targets.floating.classList.toggle(classes.active, mainBottom > window.innerHeight && submitBottom < 0);
      }
    }

    /**
     * Handles the floating button click event.
     */
    handleFloatingButtonEvent() {
      this.targets.form.scrollIntoView();
    }
  }
);
